import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { AppDispatch } from '../../redux/store';
import {
  getHousingRequestById,
  getRequestById,
  resetGetByIdRequest,
} from '../../redux/slices/HousingRequestSlice';
import { ToastType, showToastMessage } from '../../utils/ToastOptionCommon';
import DashboardHeader from '../../components/DashboardComponents/DashboardHeader/DashboardHeader';
import { Link } from 'react-router-dom';
import Loader from '../../components/CommonComponents/Loader/Loader';
import './HousingRequest.scss';

const ShowHousingDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const res = useSelector(getRequestById);
  const [data, setData] = useState<any>({});

  useEffect(() => {
    dispatch(getHousingRequestById({ id }));
  }, [id]);

  useEffect(() => {
    if (res.error) {
      showToastMessage(res.error, ToastType.Error);
    }
  }, [res.error]);

  useEffect(() => {
    if (res.success) {
      setData(res.data);
      dispatch(resetGetByIdRequest());
    }
  }, [res.success]);

  const FillDetails = ({ data, isAgency = false }: any) => {
    if (!data) return null;

    // Helper to render consistent data tiles
    const renderDetailTile = (
      label: string,
      value: any,
      colSize: string = 'col-sm-6'
    ) => {
      if (value === undefined || value === null || value === '') return null;

      return (
        <div className={colSize}>
          <div className="p-3 rounded-3 bg-light border-0 h-100">
            <span
              className="text-muted d-block mb-1 text-uppercase fw-semibold"
              style={{ fontSize: '11px', letterSpacing: '0.4px' }}
            >
              {label}
            </span>
            <span
              className="fw-semibold text-dark fs-6"
              style={{ wordBreak: 'break-word' }}
            >
              {value}
            </span>
          </div>
        </div>
      );
    };

    const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();

    return (
      <>
        {renderDetailTile('Full Name', fullName || 'N/A')}
        {!isAgency && renderDetailTile('Email Address', data.email)}
        {!isAgency && renderDetailTile('Phone Number', data.phone)}
        {renderDetailTile('Move In Date', data.moveIn)}
        {renderDetailTile('Travelling Reason', data.travelReason)}
        {renderDetailTile('Occupation / Work', data.working)}
        {renderDetailTile('Occupants', data.occupant)}
        {renderDetailTile('Property Type', data.propertyType)}
        {renderDetailTile('Budget', data.budget)}
        {renderDetailTile('Pet Preference', data.withPet)}
        {!isAgency && renderDetailTile('Contacted Via', data.contacted)}
        {/* Full-width tile for long description */}
        {renderDetailTile('Description', data.description, 'col-12')}
      </>
    );
  };

  return (
    <>
      <div className="main-content">
        <div className="">
          <DashboardHeader />

          <div className="below-header">
            <div className="row align-items-center">
              <div className="col-md-7">
                <h1>Request Detail</h1>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/dashboard/request">Request</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Request Detail
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>

          <div className="box-view py-3">
            {res.loading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Loader />
              </div>
            ) : (
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                {/* Header Banner */}
                <div
                  className="px-4 py-3 d-flex align-items-center justify-content-between border-bottom"
                  style={{
                    background:
                      'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: '#ffffff',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="rounded-circle d-inline-block"
                      style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#34d399',
                      }}
                    />
                    <h4 className="mb-0 fw-bold fs-5 text-white">
                      {data.requester} Details
                    </h4>
                  </div>

                  <span
                    className="badge px-3 py-2 rounded-pill fw-semibold text-uppercase"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      fontSize: '11px',
                      letterSpacing: '0.8px',
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    {data.requester}
                  </span>
                </div>

                {/* Body Container */}
                <div className="card-body p-4">
                  {/* Core Location Section */}
                  <div className="mb-4">
                    <div
                      className="text-uppercase text-muted fw-bold mb-3"
                      style={{ fontSize: '11px', letterSpacing: '0.5px' }}
                    >
                      📍 Location Details
                    </div>
                    <div className="row g-3">
                      <div className="col-sm-6">
                        <div className="p-3 rounded-3 bg-light border-0">
                          <span className="text-muted small d-block mb-1">
                            State
                          </span>
                          <span className="fw-semibold text-dark fs-6">
                            {data.state || 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="p-3 rounded-3 bg-light border-0">
                          <span className="text-muted small d-block mb-1">
                            City
                          </span>
                          <span className="fw-semibold text-dark fs-6">
                            {data.city || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tenant / Traveler Dynamic Section */}
                  {(data.requester === 'Tenant' ||
                    data.requester === 'Traveler') && (
                    <div className="pt-3 border-top">
                      <div
                        className="text-uppercase text-muted fw-bold mb-3"
                        style={{ fontSize: '11px', letterSpacing: '0.5px' }}
                      >
                        👤 Personal Details
                      </div>
                      {/* Custom wrapper to strip list styles and format grid */}
                      <div className="fill-details-wrapper">
                        <div className="row g-3 list-unstyled m-0">
                          <FillDetails
                            data={data[data.requester?.toLowerCase()]}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Agency Dynamic Section */}
                  {data.requester === 'Agency' && (
                    <div className="pt-3 border-top">
                      <div
                        className="text-uppercase text-muted fw-bold mb-3"
                        style={{ fontSize: '11px', letterSpacing: '0.5px' }}
                      >
                        🏢 Agency Details
                      </div>
                      <div className="row g-3 mb-4">
                        <div className="col-sm-4">
                          <div className="p-3 rounded-3 bg-light border-0 h-100">
                            <span className="text-muted small d-block mb-1">
                              Agency Name
                            </span>
                            <span className="fw-semibold text-dark">
                              {data.agency?.name || 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <div className="p-3 rounded-3 bg-light border-0 h-100">
                            <span className="text-muted small d-block mb-1">
                              Email Address
                            </span>
                            <span className="fw-semibold text-dark">
                              {data.agency?.email || 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <div className="p-3 rounded-3 bg-light border-0 h-100">
                            <span className="text-muted small d-block mb-1">
                              Phone Number
                            </span>
                            <span className="fw-semibold text-dark">
                              {data.agency?.contact || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Traveler List */}
                      {data?.agencies?.length > 0 && (
                        <div className="pt-2">
                          <div
                            className="text-uppercase text-muted fw-bold mb-3"
                            style={{ fontSize: '11px', letterSpacing: '0.5px' }}
                          >
                            🧳 Associated Travelers ({data.agencies.length})
                          </div>

                          <div className="d-flex flex-column gap-3">
                            {data.agencies.map((e: any, idx: number) => (
                              <div
                                key={e?._id || idx}
                                className="p-3.5 p-md-4 rounded-4 border"
                                style={{
                                  backgroundColor: '#f8fafc',
                                  borderColor: '#e2e8f0',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                                  <span
                                    className="badge rounded-circle text-white d-inline-flex align-items-center justify-content-center"
                                    style={{
                                      width: '22px',
                                      height: '22px',
                                      backgroundColor: '#328995',
                                      fontSize: '11px',
                                    }}
                                  >
                                    {idx + 1}
                                  </span>
                                  <h6 className="fw-bold text-dark mb-0">
                                    Traveler #{idx + 1}
                                  </h6>
                                </div>

                                <div className="fill-details-wrapper">
                                  <div className="row g-3 list-unstyled m-0">
                                    <FillDetails data={e} isAgency={true} />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShowHousingDetail;
